import uniq from 'lodash/uniq'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { isPending, trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { Alert, exportDataToCSV, formatDate } from '@avantstay/backoffice-core'

import { QuotesFormView } from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/CreateQuoteForm.types'
import { CreateQuoteFormStore } from '@new/domains/bookingsTapeChart/stores/CreateQuoteFormStore'
import {
  HomeFilterIds,
  TapeChartCreateTapeStore,
} from '@new/domains/fastBookingsTapeChart/internal'
import { fastBookingsTapeChartFiltersStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartFiltersStore'
import { fastBookingsTapeChartStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'
import { selectRangeStore } from '@new/domains/fastBookingsTapeChart/stores/SelectRangeStore'
import { quotesExportableFields } from '@new/domains/marketing/screens/Quotes/definitions'

import { getPreBookingPriceTotal } from '@arriere/modules/bookingPrices'
import { getHomeDataToExportQuotes } from '@arriere/modules/quotes'
import { PreBookingPriceQuote } from '@arriere/types/BookingPrices.types'
import { trackError } from '@tracking/customEventReport'

export class _TapeChartQuotesStore {
  // Observables
  homesForQuotesPreBookingPrice?: PreBookingPriceQuote[]

  constructor() {
    makeObservable(this, {
      homesForQuotesPreBookingPrice: observable,
      selectionQuotesHomeIds: computed,
      canCreateQuote: computed,
      hasMultipleHomesSelected: computed,
      shouldDisplayQuotesHeader: computed,
      setPreBookingPriceArray: action.bound,
      deselectHomeForQuotes: action.bound,
      showQuotesModal: action.bound,
    })

    this.exportPropertyInfoCSV = trackedAction<any>(this.exportPropertyInfoCSV.bind(this))
    this.getHomeData = trackedAction<any>(this.getHomeData.bind(this))

    reaction(
      () => fastBookingsTapeChartFiltersStore.availableDates,
      () => this.getNewData(),
    )
  }

  get selectionQuotesHomeIds() {
    const hasValidIds = TapeChartCreateTapeStore.createTapeContext?.some(
      tapeContext => !!tapeContext.home?.id,
    )

    return hasValidIds
      ? (TapeChartCreateTapeStore.createTapeContext?.map(
          tapeContext => tapeContext.home?.id,
        ) as string[])
      : []
  }

  // Computed values
  get canCreateQuote() {
    return !!this.selectionQuotesHomeIds?.length && this.selectionQuotesHomeIds?.length >= 1
  }

  get hasMultipleHomesSelected() {
    return !!this.selectionQuotesHomeIds?.length && this.selectionQuotesHomeIds?.length > 1
  }

  get shouldDisplayQuotesHeader() {
    return (
      this.canCreateQuote && CreateQuoteFormStore.quotesFormView === QuotesFormView.emailPreview
    )
  }

  getSelectedHomesForQuotesState = computedFn(
    () => this.selectionQuotesHomeIds && this.selectionQuotesHomeIds.length > 0,
  )

  getTotalPriceForSelectedQuote = computedFn((homeId: string) => {
    return this.homesForQuotesPreBookingPrice?.find(homePrice => homePrice.homeId === homeId)
  })

  getSelectedHomeForQuote = computedFn((homeId: string) => {
    return !!this.selectionQuotesHomeIds?.find(selectedId => selectedId === homeId)
  })

  isLoadingHomeData = computedFn((homeId: string) => {
    const alreadyHasData = this.homesForQuotesPreBookingPrice?.some(price =>
      price.homeId?.includes(homeId),
    )

    return !alreadyHasData && isPending(this.getHomeData)
  })

  getNewData = computedFn(() => {
    if (this.selectionQuotesHomeIds?.length) {
      return uniq(this.selectionQuotesHomeIds)?.map(homeId => this.getHomeData(homeId))
    }
  })

  getStayLimits = computedFn((homeId?: string, fromDateRange?: string) => {
    if (fromDateRange && fastBookingsTapeChartStore.selectRangeProps.datePrices) {
      const rangeStart = formatDate(fromDateRange)

      const datePrice = fastBookingsTapeChartStore.selectRangeProps.datePrices.find(
        datePrice => formatDate(datePrice.date) === rangeStart && datePrice.homeId === homeId,
      )

      return {
        maxStay: datePrice?.maxStay || undefined,
        minStay: datePrice?.minStay || undefined,
      }
    }
    return {
      maxStay: undefined,
      minStay: undefined,
    }
  })

  private dataIsAlreadyAvailable(homeId?: string, fromDate?: string, toDate?: string): boolean {
    return (
      !!homeId &&
      !!this.homesForQuotesPreBookingPrice?.find(
        price =>
          price.homeId?.includes(homeId) &&
          fromDate === price.range.fromDate &&
          toDate === price.range.toDate,
      )
    )
  }

  private prepareHomeData() {
    const { availableDates } = fastBookingsTapeChartFiltersStore
    const { createTapeContext } = TapeChartCreateTapeStore

    // When quotes tool is not active, but we select range, it should use context period
    const tapeContextDates =
      createTapeContext && createTapeContext?.length <= 1
        ? createTapeContext?.[0]?.period
        : undefined

    if (!this.selectionQuotesHomeIds && (!availableDates || !tapeContextDates)) {
      return undefined
    }

    const fromDate = availableDates ? formatDate(availableDates?.start) : tapeContextDates?.fromDate
    const toDate = availableDates ? formatDate(availableDates?.end) : tapeContextDates?.toDate

    return { fromDate, toDate }
  }

  private insertPreBookingPrice(quote: PreBookingPriceQuote) {
    this.homesForQuotesPreBookingPrice = [...(this.homesForQuotesPreBookingPrice || []), quote]
  }

  private updatePreBookingPrice(quote: PreBookingPriceQuote, index: number) {
    if (!this.homesForQuotesPreBookingPrice) return
    this.homesForQuotesPreBookingPrice[index] = quote
  }

  private async getPrebookedPriceData(
    homeId?: string,
    fromDate?: string,
    toDate?: string,
    maxStay?: number,
    minStay?: number,
  ) {
    const { filteringArgs } = fastBookingsTapeChartFiltersStore

    const guestsFilter = filteringArgs[HomeFilterIds.Guests]
    const petsFilter = filteringArgs[HomeFilterIds.MinPets]

    if (!homeId || !fromDate || !toDate) {
      return Promise.reject('Missing pre booking price arguments')
    }

    return await getPreBookingPriceTotal({
      homeId: homeId,
      from: fromDate,
      until: toDate,
      guests: guestsFilter?.from || 1,
      pets: petsFilter || 0,
    }).then(response => {
      return {
        homeId,
        maxStay,
        minStay,
        range: { fromDate, toDate },
        total: response?.total.total,
        subtotal: response?.total.subtotal,
        deposits: response?.total.deposits,
      }
    })
  }

  private addPriceData(priceData: PreBookingPriceQuote) {
    this.setPreBookingPriceArray(priceData)
  }

  // Actions

  async getHomeData(homeId?: string) {
    const dateRange = this.prepareHomeData()

    if (
      !dateRange?.fromDate ||
      !dateRange?.toDate ||
      this.dataIsAlreadyAvailable(homeId, dateRange.fromDate, dateRange.toDate)
    ) {
      return Promise.resolve(undefined)
    }

    const hasAllRates = selectRangeStore.getRangeHasNightlyPrices({
      homeId,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    })

    if (hasAllRates) {
      const { maxStay, minStay } = this.getStayLimits(homeId, dateRange.fromDate)

      const priceData = await this.getPrebookedPriceData(
        homeId,
        dateRange?.fromDate,
        dateRange?.toDate,
        maxStay,
        minStay,
      )

      this.addPriceData(priceData)
    }
  }

  setPreBookingPriceArray(quote: PreBookingPriceQuote) {
    const index = this.homesForQuotesPreBookingPrice?.findIndex(
      home => home.homeId === quote.homeId,
    )
    if (index !== undefined && index !== -1) {
      this.updatePreBookingPrice(quote, index)
    } else {
      this.insertPreBookingPrice(quote)
    }
  }

  deselectHomeForQuotes() {
    if (!this.selectionQuotesHomeIds) {
      return
    }

    TapeChartCreateTapeStore.createTapeContext = undefined
  }

  showQuotesModal() {
    if (!this.selectionQuotesHomeIds) {
      return
    }
  }

  async exportPropertyInfoCSV() {
    const homeIds = this.selectionQuotesHomeIds || []
    const { availableDates } = fastBookingsTapeChartFiltersStore

    try {
      const homesData = await getHomeDataToExportQuotes(homeIds)

      const data = homesData.map(home => ({
        id: home.id,
        name: home.name,
        address: home.address.address,
        city: home.address.city,
        zipCode: home.address.zipCode,
        region: home.region?.name,
        bedroomsCount: home.bedroomsCount,
        bathroomsCount: home.bathroomsCount,
        halfBathroomsCount: home.halfBathroomsCount,
        maxOccupancy: home.maxOccupancy,
        petPolicy: home.petPolicy,
      }))

      exportDataToCSV(data, quotesExportableFields, {
        filename: `${availableDates?.start}_${availableDates?.end}-quotes`,
      })

      Alert.Success('CSV file created successfully')
    } catch (error) {
      const errorMessage = `Export Quotes CSV:`
      trackError(errorMessage, { error })
      Alert.Error(`${errorMessage} ${error}`)
    }
  }
}

export const TapeChartQuotesStore = new _TapeChartQuotesStore()
